import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getSettings,
  updateSettings,
  uploadImageLogoClub,
} from "../../../../services/settingsServices";
import { getAllGalleries } from "../../../../services/gallerieServices";
import Club from "./Club";
import Coordonnees from "./Coordonnees";
import Accueil from "./Accueil";
import Button from "../../../../components/Button/Button";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  const base = String(BASE_URL).replace(/\/$/, "");
  const path = String(url).startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
};

const DEFAULT_SETTINGS = {
  nom_club: "",
  shortname_club: "",
  responsable_club: "",
  logo_club: "",
  adresse_club: "",
  commune_club: "",
  tel_club: "",
  mail_club: "",
  siteweb_club: "",
  siret_club: "",
  facebook_club: "",
  instagram_club: "",
  primary_color: "#000000",
  about_title_fr: "",
  about_title_en: "",
  about_content_fr: "",
  about_content_en: "",
  carrousel_id: "",
  header1_photo_url: "",
  header2_photo_url: "",
  header_title_fr: "",
  header_title_en: "",
  header_subtitle_fr: "",
  header_subtitle_en: "",
};

const KEYS_FOR_DIRTY = Object.keys(DEFAULT_SETTINGS);

const pickComparable = (obj) => {
  const out = {};
  for (const k of KEYS_FOR_DIRTY) out[k] = obj?.[k] ?? "";
  return out;
};

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      "px-4 py-2 rounded-xl text-sm font-bold border transition " +
      (active
        ? "bg-primary text-white border-primary"
        : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800")
    }
  >
    {children}
  </button>
);

const Card = ({ title, desc, children }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
      <div className="text-lg font-extrabold text-gray-900 dark:text-gray-100">
        {title}
      </div>
      {desc ? (
        <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{desc}</div>
      ) : null}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

export default function Parametres() {
  const location = useLocation();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const settingsRef = useRef(settings);
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const [initialSettings, setInitialSettings] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const [tab, setTab] = useState("club"); // club | coords | accueil

  // Hash => tab (pratique si tu veux /parametres#accueil)
  useEffect(() => {
    const h = (location.hash || "").replace("#", "").trim();
    if (h === "club" || h === "coords" || h === "accueil") setTab(h);
  }, [location.hash]);

  const disabled = isLoading || isDemoMode;

  const dirty = useMemo(() => {
    if (!initialSettings) return false;
    const a = JSON.stringify(pickComparable(initialSettings));
    const b = JSON.stringify(pickComparable(settings));
    return a !== b;
  }, [initialSettings, settings]);

  const checkDemoMode = useCallback(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && Number(user.role_id) === 4) setIsDemoMode(true);
    } catch {
      // ignore
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [settingsData, galleriesData] = await Promise.all([
        getSettings(),
        getAllGalleries(),
      ]);
      const s = settingsData?.[0] ? { ...DEFAULT_SETTINGS, ...settingsData[0] } : DEFAULT_SETTINGS;
      setSettings(s);
      setInitialSettings(s);
      setGalleries(Array.isArray(galleriesData) ? galleriesData : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des paramètres.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkDemoMode();
    fetchAll();
  }, [checkDemoMode, fetchAll]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const resetToInitial = () => {
    if (!initialSettings) return;
    setSettings(initialSettings);
    toast.info("Modifications annulées.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDemoMode) {
      toast.info("Fonction désactivée en mode Démonstration");
      return;
    }
    setIsLoading(true);
    try {
      await updateSettings(settingsRef.current);
      setInitialSettings(settingsRef.current);
      toast.success("Paramètres enregistrés ✅");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isDemoMode) {
      toast.info("Upload désactivé en mode Démonstration");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("category", "settings");

    setIsLoading(true);
    try {
      const uploadResponse = await uploadImageLogoClub(formData);
      if (!uploadResponse?.filePath) throw new Error("filePath manquant");

      setSettings((prev) => ({ ...prev, logo_club: uploadResponse.filePath }));
      toast.success("Logo téléversé ✅ Pense à enregistrer.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload du logo.");
    } finally {
      setIsLoading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">
              Paramètres
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Modifie les informations du club et la page d’accueil.{" "}
              <span className="font-semibold">N’oublie pas d’enregistrer.</span>
            </p>
          </div>

          {isDemoMode ? (
            <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              Mode Démonstration
            </span>
          ) : null}
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          <TabButton active={tab === "club"} onClick={() => setTab("club")}>
            Club
          </TabButton>
          <TabButton active={tab === "coords"} onClick={() => setTab("coords")}>
            Coordonnées
          </TabButton>
          <TabButton active={tab === "accueil"} onClick={() => setTab("accueil")}>
            Page d’accueil
          </TabButton>

          <div className="ml-auto flex items-center gap-2">
            {dirty ? (
              <span className="text-xs font-bold text-orange-700 dark:text-orange-300">
                ● Modifications non enregistrées
              </span>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Tout est à jour
              </span>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        {tab === "club" && (
          <>
            <Card
              title="Identité du club"
              desc="Nom, responsable, SIRET…"
            >
              <Club settings={settings} handleChange={handleChange} disabled={disabled} />
            </Card>

            <Card
              title="Logo"
              desc="Téléverse ton logo. L’upload met à jour l’aperçu, puis enregistre pour persister."
            >
              <div className="flex flex-col md:flex-row gap-5 items-start">
                <div className="w-full md:w-1/3">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
                    Aperçu
                  </div>
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 flex items-center justify-center min-h-[160px]">
                    {settings.logo_club ? (
                      <img
                        src={toAbs(settings.logo_club)}
                        alt="Logo"
                        className="max-h-32 object-contain"
                      />
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Aucun logo
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 w-full">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
                    Changer le logo
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    disabled={disabled}
                    className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-gray-100 file:text-gray-800 hover:file:bg-gray-200 dark:file:bg-gray-800 dark:file:text-gray-100 dark:hover:file:bg-gray-700"
                  />
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Conseillé : PNG/SVG, fond transparent, largeur ~512px.
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}

        {tab === "coords" && (
          <Card title="Coordonnées" desc="Adresse, téléphone, réseaux, couleur primaire…">
            <Coordonnees
              settings={settings}
              handleChange={handleChange}
              disabled={disabled}
            />
          </Card>
        )}

        {tab === "accueil" && (
          <Card title="Page d’accueil" desc="Images header, textes, carrousel…">
            <Accueil
              settings={settings}
              setSettings={setSettings}
              handleChange={handleChange}
              galleries={galleries}
              disabled={disabled}
            />
          </Card>
        )}

        {/* Barre sticky actions */}
        <div className="sticky bottom-3 z-40">
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur shadow-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-gray-700 dark:text-gray-200">
              {dirty ? (
                <span className="font-bold text-orange-700 dark:text-orange-300">
                  Modifications en attente
                </span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">
                  Aucune modification
                </span>
              )}
              {isLoading ? (
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                  · Sauvegarde/chargement…
                </span>
              ) : null}
            </div>

            <div className="flex gap-2">
              <Button
                text="Réinitialiser"
                type="button"
                disabled={!dirty || disabled}
                className="btn-ghost"
                onClick={resetToInitial}
              />
              <Button
                text="Enregistrer"
                type="submit"
                disabled={!dirty || disabled}
                className="btn-primary"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
